import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, DeleteOutline, EditOutlined, Reply, Send, ThumbUp, ThumbUpOutlined } from '@mui/icons-material';
import {
  NewsArticle,
  NewsComment,
  NewsCommentsResult,
  NewsEngagementSummary,
  NewsFeedItem,
  NewsItemAnalytics,
} from '../../../app/services/api/news/newsConsumerTypes';
import { useNewsConsumerClient } from '../../../hooks/api/useNewsConsumerClient';
import { getDefaultNewsImage } from '../../../assets';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';

interface NewsModalProps {
  open: boolean;
  item: NewsFeedItem | null;
  onClose: () => void;
  mode?: 'Current' | 'History';
  storeNumber?: string;
  onRead?: (publicationId: number) => void;
}

export function NewsModal({ open, item, onClose, mode = 'Current', storeNumber, onRead }: NewsModalProps) {
  const { t } = useTranslation('drawer');
  const { hasPermissionTo, user } = useContext(UserContext);
  const canModerate = hasPermissionTo(['NewsModerate']);
  const canViewAnalytics = hasPermissionTo(['NewsAnalyticsView']);
  const client = useNewsConsumerClient();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [analytics, setAnalytics] = useState<NewsItemAnalytics | null>(null);
  const [engagement, setEngagement] = useState<NewsEngagementSummary | null>(null);
  const [comments, setComments] = useState<NewsCommentsResult | null>(null);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<NewsComment | null>(null);
  const [moderationTarget, setModerationTarget] = useState<NewsComment | null>(null);
  const [moderationReason, setModerationReason] = useState('');
  const [editingComment, setEditingComment] = useState<NewsComment | null>(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [imageError, setImageError] = useState(false);

  const refreshEngagement = async (newsItemId: number, loadedArticle: NewsArticle) => {
    if (mode !== 'Current') return;

    setEngagementLoading(true);
    try {
      const [summary, thread] = await Promise.all([
        loadedArticle.reactionsEnabled ? client.getEngagement(newsItemId) : Promise.resolve(null),
        loadedArticle.commentsEnabled ? client.getComments(newsItemId) : Promise.resolve(null),
      ]);

      setEngagement(summary);
      setComments(thread);
    } finally {
      setEngagementLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setAnalytics(null);
    setEngagement(null);
    setComments(null);
    setNewComment('');
    setReplyTo(null);
    setModerationTarget(null);
    setModerationReason('');
    setEditingComment(null);
    setEditContent('');
    setLoadFailed(false);
    setImageError(false);

    if (!open || !item) return () => { cancelled = true; };

    setLoading(true);
    client.getArticle(item.newsItemId, mode, storeNumber)
      .then(async result => {
        if (cancelled) return;
        setArticle(result);
        onRead?.(result.publicationId);

        if (mode === 'Current') {
          client.recordView(item.newsItemId, crypto.randomUUID()).catch(() => undefined);
        }

        if (canViewAnalytics) {
          client.getAnalytics(item.newsItemId)
            .then(value => {
              if (!cancelled) setAnalytics(value);
            })
            .catch(() => undefined);
        }

        if (mode === 'Current') {
          try {
            await refreshEngagement(item.newsItemId, result);
          } catch {
            if (!cancelled) {
              setEngagement(null);
              setComments(null);
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canViewAnalytics, client, item, mode, onRead, open, storeNumber]);

  const handleArticleReaction = async () => {
    if (!item || !article?.reactionsEnabled || !engagement) return;
    setSubmitting(true);
    try {
      setEngagement(await client.setReaction(item.newsItemId, !engagement.reactedByCurrentUser));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!item || !article?.commentsEnabled || !newComment.trim()) return;
    setSubmitting(true);
    try {
      await client.createComment(item.newsItemId, newComment.trim(), replyTo?.id);
      setNewComment('');
      setReplyTo(null);
      setComments(await client.getComments(item.newsItemId));
      if (article.reactionsEnabled) {
        setEngagement(await client.getEngagement(item.newsItemId));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentReaction = async (comment: NewsComment) => {
    if (!item || !article?.reactionsEnabled || comment.isDeleted) return;
    setSubmitting(true);
    try {
      await client.setCommentReaction(
        item.newsItemId,
        comment.id,
        !comment.reactedByCurrentUser,
      );
      if (article.commentsEnabled) {
        setComments(await client.getComments(item.newsItemId));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateOwnComment = async () => {
    if (!item || !editingComment || !editContent.trim()) return;
    setSubmitting(true);
    try {
      await client.updateComment(
        item.newsItemId,
        editingComment.id,
        editContent.trim(),
        editingComment.rowVersion,
      );
      setComments(await client.getComments(item.newsItemId));
      setEditingComment(null);
      setEditContent('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOwnComment = async (comment: NewsComment) => {
    if (!item || comment.userId !== user?.id) return;
    setSubmitting(true);
    try {
      await client.deleteOwnComment(item.newsItemId, comment.id, comment.rowVersion);
      setComments(await client.getComments(item.newsItemId));
      if (article?.reactionsEnabled) {
        setEngagement(await client.getEngagement(item.newsItemId));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleModerateComment = async () => {
    if (!item || !moderationTarget || !moderationReason.trim()) return;
    setSubmitting(true);
    try {
      await client.moderateComment(
        item.newsItemId,
        moderationTarget.id,
        moderationReason.trim(),
        moderationTarget.rowVersion,
      );
      setComments(await client.getComments(item.newsItemId));
      if (article?.reactionsEnabled) {
        setEngagement(await client.getEngagement(item.newsItemId));
      }
      setModerationTarget(null);
      setModerationReason('');
    } finally {
      setSubmitting(false);
    }
  };

  const renderComment = (comment: NewsComment, isReply = false) => (
    <Box
      key={comment.id}
      sx={{
        pl: isReply ? 3 : 0,
        borderLeft: isReply ? '2px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}>
        <Typography variant="textSM"
          fontWeight={600}>
          {comment.userDisplayName || `${t('newsUser')} ${comment.userId}`}
        </Typography>
        <Typography variant="textSM"
          color="text.secondary">
          {new Date(comment.createdAtUtc).toLocaleString()}
          {comment.editedAtUtc ? ` · ${t('newsEdited')}` : ''}
        </Typography>
      </Stack>

      {editingComment?.id === comment.id ? (
        <Stack spacing={1}
          sx={{ mt: 1 }}>
          <TextField
            size="small"
            multiline
            minRows={2}
            maxRows={5}
            inputProps={{ maxLength: 2000, 'aria-label': 'Edit comment' }}
            value={editContent}
            onChange={event => setEditContent(event.target.value)}
            disabled={submitting}
          />
          <Stack direction="row"
            spacing={1}>
            <Button
              size="md"
              disabled={submitting || !editContent.trim()}
              onClick={handleUpdateOwnComment}
            >
              Save
            </Button>
            <Button
              size="md"
              onClick={() => {
                setEditingComment(null);
                setEditContent('');
              }}
            >
              {t('newsCancel')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="textSM"
          color={comment.isDeleted ? 'text.secondary' : 'text.primary'}
          sx={{ mt: 0.5 }}>
          {comment.isDeleted ? t('newsCommentRemoved') : comment.content}
        </Typography>
      )}

      {!comment.isDeleted && editingComment?.id !== comment.id && (
        <Stack direction="row"
          spacing={1}
          sx={{ mt: 1 }}>
          {article?.reactionsEnabled && (
            <Button
              size="md"
              variant="secondary"
              startIcon={comment.reactedByCurrentUser ? <ThumbUp /> : <ThumbUpOutlined />}
              disabled={submitting}
              onClick={() => handleCommentReaction(comment)}
            >
              {comment.reactionCount}
            </Button>
          )}
          {!isReply && article?.commentsEnabled && (
            <Button
              size="md"
              variant="secondary"
              startIcon={<Reply />}
              onClick={() => setReplyTo(comment)}
            >
              {t('newsReply')}
            </Button>
          )}
          {comment.userId === user?.id && (
            <>
              <IconButton
                size="small"
                aria-label="Edit comment"
                disabled={submitting}
                onClick={() => {
                  setEditingComment(comment);
                  setEditContent(comment.content);
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Delete comment"
                disabled={submitting}
                onClick={() => handleDeleteOwnComment(comment)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </>
          )}
          {canModerate && (
            <Button
              size="md"
              variant="badSecondary"
              disabled={submitting}
              onClick={() => {
                setModerationTarget(comment);
                setModerationReason('');
              }}
            >
              {t('newsRemoveComment')}
            </Button>
          )}
        </Stack>
      )}

      {comment.replies.length > 0 && (
        <Stack spacing={2}
          sx={{ mt: 2 }}>
          {comment.replies.map(reply => renderComment(reply, true))}
        </Stack>
      )}

      {moderationTarget?.id === comment.id && (
        <Stack spacing={1}
          sx={{ mt: 2 }}>
          <TextField
            size="small"
            multiline
            maxRows={3}
            value={moderationReason}
            onChange={event => setModerationReason(event.target.value)}
            placeholder={t('newsModerationReason')}
            inputProps={{ maxLength: 500 }}
          />
          <Stack direction="row"
            spacing={1}>
            <Button
              variant="bad"
              size="md"
              disabled={submitting || !moderationReason.trim()}
              onClick={handleModerateComment}
            >
              {t('newsConfirmRemove')}
            </Button>
            <Button
              size="md"
              onClick={() => {
                setModerationTarget(null);
                setModerationReason('');
              }}
            >
              {t('newsCancel')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );

  const hero = article?.media.find(media => media.purpose === 'Hero');
  const attachments = article?.media.filter(media => media.purpose === 'Attachment' && media.url) ?? [];
  const imageUrl = hero?.url && !imageError
    ? hero.url
    : item ? getDefaultNewsImage(Math.abs(item.newsItemId) % 4) : undefined;

  return (
    <Dialog open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <IconButton onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Close />
      </IconButton>

      {item && imageUrl && (
        <DialogTitle sx={{ p: 0 }}>
          <Box
            component="img"
            src={imageUrl}
            alt={hero?.altText || item.title}
            onError={() => setImageError(true)}
            sx={{ width: '100%', height: 220, objectFit: 'cover' }}
          />
        </DialogTitle>
      )}

      <DialogContent sx={{ p: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : loadFailed || !article ? (
          <Typography color="text.secondary">{t('newsLoadFailed')}</Typography>
        ) : (
          <>
            <Stack direction="row"
              spacing={1}
              flexWrap="wrap"
              mb={2}>
              {article.readState.isUpdated && <Chip label={t('newsUpdated')}
                color="warning"
                size="small" />}
              {article.tags.map(tag => <Chip key={tag.id}
                label={tag.name}
                size="small" />)}
            </Stack>

            <Typography variant="textLG"
              fontWeight={700}
              sx={{ fontSize: 24, mb: 1 }}>
              {article.title}
            </Typography>
            <Typography variant="textSM"
              color="text.secondary"
              sx={{ mb: 4 }}>
              {new Date(article.publishedAtUtc).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Typography>

            <Box
              sx={{
                '& p': { mb: 2 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: '4px' },
              }}
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            {attachments.length > 0 && (
              <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="textMD"
                  fontWeight={700}
                  sx={{ mb: 2 }}>
                  Attachments
                </Typography>
                <Stack spacing={1}>
                  {attachments.map(attachment => (
                    <Button
                      key={attachment.id}
                      component="a"
                      href={attachment.url ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="secondary"
                      size="md"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {attachment.originalFileName}
                    </Button>
                  ))}
                </Stack>
              </>
            )}

            {canViewAnalytics && analytics && analytics.publications.length > 0 && (() => {
              const publication = analytics.publications.find(x => x.publicationId === article.publicationId)
                ?? analytics.publications[analytics.publications.length - 1];

              return (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Typography variant="textMD"
                    fontWeight={700}
                    sx={{ mb: 2 }}>
                    {t('newsPerformance')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    flexWrap="wrap">
                    <Chip label={`${t('newsViews')}: ${publication.totalViews}`} />
                    <Chip label={`${t('newsUniqueViewers')}: ${publication.uniqueViewers}`} />
                    <Chip label={`${t('newsLocationReach')}: ${publication.locationReachPercent}%`} />
                    <Chip label={`${t('newsEngagementRate')}: ${publication.engagementRatePercent}%`} />
                  </Stack>
                  <Typography variant="textXS"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    {t('newsAnalyticsAsOf')} {new Date(analytics.asOfUtc).toLocaleString()}
                  </Typography>
                </>
              );
            })()}

            {mode === 'Current' && (article.commentsEnabled || article.reactionsEnabled) && (
              <>
                <Divider sx={{ my: 4 }} />

                {engagementLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    {article.reactionsEnabled && engagement && (
                      <Button
                        size="md"
                        variant={engagement.reactedByCurrentUser ? 'primary' : 'secondary'}
                        startIcon={engagement.reactedByCurrentUser ? <ThumbUp /> : <ThumbUpOutlined />}
                        disabled={submitting}
                        onClick={handleArticleReaction}
                        sx={{ mb: article.commentsEnabled ? 3 : 0 }}
                      >
                        {t('newsLike')} ({engagement.reactionCount})
                      </Button>
                    )}

                    {article.commentsEnabled && comments && (
                      <Stack spacing={3}>
                        <Typography variant="textMD"
                          fontWeight={700}>
                          {t('newsComments')} ({comments.totalCount})
                        </Typography>

                        {comments.comments.length === 0 ? (
                          <Typography variant="textSM"
                            color="text.secondary">
                            {t('newsNoComments')}
                          </Typography>
                        ) : (
                          <Stack spacing={3}>
                            {comments.comments.map(comment => renderComment(comment))}
                          </Stack>
                        )}

                        {replyTo && (
                          <Chip
                            label={`${t('newsReplyingTo')} ${replyTo.userDisplayName || `${t('newsUser')} ${replyTo.userId}`}`}
                            onDelete={() => setReplyTo(null)}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}

                        <Stack direction="row"
                          spacing={1}
                          alignItems="flex-end">
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={5}
                            inputProps={{ maxLength: 2000 }}
                            value={newComment}
                            onChange={event => setNewComment(event.target.value)}
                            placeholder={replyTo ? t('newsWriteReply') : t('newsWriteComment')}
                            disabled={submitting}
                          />
                          <IconButton
                            color="primary"
                            disabled={submitting || !newComment.trim()}
                            onClick={handleAddComment}
                          >
                            {submitting ? <CircularProgress size={20} /> : <Send />}
                          </IconButton>
                        </Stack>
                      </Stack>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
